const FEATURED_COLLECTION_VIDEO_TAG = "featured-collection-video-tile";

if (!customElements.get(FEATURED_COLLECTION_VIDEO_TAG)) {
  class FeaturedCollectionVideoTile extends HTMLElement {
    constructor() {
      super();

      this.handleControlClick = this.handleControlClick.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleVideoEnded = this.handleVideoEnded.bind(this);
      this.handleVideoError = this.handleVideoError.bind(this);
      this.handleVideoPause = this.handleVideoPause.bind(this);
      this.handleVideoPlaying = this.handleVideoPlaying.bind(this);
    }

    connectedCallback() {
      if (this.initialized || this.dataset.hasVideo !== "true") return;

      this.initialized = true;
      this.template = this.querySelector("[data-featured-collection-video-template]");
      this.videoContainer = this.querySelector("[data-featured-collection-video-container]");
      this.control = this.querySelector("[data-featured-collection-video-control]");
      this.shouldAutoplay = this.dataset.autoplay === "true";
      this.shouldLoop = this.dataset.loop === "true";
      this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.hasEnded = false;
      this.userPaused = false;
      this.inPlaybackViewport = false;

      this.control?.addEventListener("click", this.handleControlClick);
      document.addEventListener("visibilitychange", this.handleVisibilityChange);

      this.setupObservers();
    }

    disconnectedCallback() {
      this.loadObserver?.disconnect();
      this.playbackObserver?.disconnect();
      this.control?.removeEventListener("click", this.handleControlClick);
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
      this.removeVideoListeners();
      this.video?.pause();
    }

    setupObservers() {
      if (!("IntersectionObserver" in window)) {
        this.loadVideo();
        this.inPlaybackViewport = true;
        this.maybeAutoplay();
        return;
      }

      this.loadObserver = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) return;

          this.loadVideo();
          this.loadObserver.disconnect();
        },
        {
          rootMargin: "300px 0px",
          threshold: 0,
        }
      );

      this.playbackObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.inPlaybackViewport = Boolean(
            entry?.isIntersecting && entry.intersectionRatio >= 0.35
          );

          if (this.inPlaybackViewport) {
            this.loadVideo();
            this.maybeAutoplay();
          } else {
            this.pauseVideo(false);
          }
        },
        {
          threshold: [0, 0.35, 0.75],
        }
      );

      this.loadObserver.observe(this);
      this.playbackObserver.observe(this);
    }

    loadVideo() {
      if (this.video || !this.template || !this.videoContainer) return;

      const fragment = this.template.content.cloneNode(true);
      this.video = fragment.querySelector("video");
      if (!this.video) return;

      this.video.autoplay = false;
      this.video.controls = false;
      this.video.loop = this.shouldLoop;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.preload = "metadata";

      this.video.addEventListener("ended", this.handleVideoEnded);
      this.video.addEventListener("error", this.handleVideoError);
      this.video.addEventListener("pause", this.handleVideoPause);
      this.video.addEventListener("playing", this.handleVideoPlaying);

      this.videoContainer.appendChild(fragment);
      this.video.load();
    }

    removeVideoListeners() {
      if (!this.video) return;

      this.video.removeEventListener("ended", this.handleVideoEnded);
      this.video.removeEventListener("error", this.handleVideoError);
      this.video.removeEventListener("pause", this.handleVideoPause);
      this.video.removeEventListener("playing", this.handleVideoPlaying);
    }

    maybeAutoplay() {
      if (
        !this.shouldAutoplay ||
        !this.inPlaybackViewport ||
        document.hidden ||
        this.prefersReducedMotion.matches ||
        this.userPaused ||
        (this.hasEnded && !this.shouldLoop)
      ) {
        return;
      }

      this.playVideo(false);
    }

    playVideo(fromUser) {
      this.loadVideo();
      if (!this.video) return;

      if (fromUser) {
        this.userPaused = false;

        if (this.hasEnded) {
          this.video.currentTime = 0;
          this.hasEnded = false;
        }
      }

      const playPromise = this.video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          this.updateControl(false);
        });
      }
    }

    pauseVideo(fromUser) {
      if (!this.video) return;

      if (fromUser) {
        this.userPaused = true;
      }

      this.video.pause();
    }

    handleControlClick(event) {
      event.preventDefault();
      event.stopPropagation();

      this.loadVideo();
      if (!this.video) return;

      if (this.video.paused || this.video.ended) {
        this.playVideo(true);
      } else {
        this.pauseVideo(true);
      }
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.pauseVideo(false);
      } else {
        this.maybeAutoplay();
      }
    }

    handleVideoPlaying() {
      this.classList.add("has-played");
      this.updateControl(true);
    }

    handleVideoPause() {
      this.updateControl(false);
    }

    handleVideoEnded() {
      this.hasEnded = true;
      this.updateControl(false);
    }

    handleVideoError() {
      this.classList.remove("has-played");
      this.updateControl(false);
    }

    updateControl(isPlaying) {
      if (!this.control) return;

      const accessibilityStrings = window.theme?.strings?.accessibility;
      const playLabel = accessibilityStrings?.play_video || "Play video";
      const pauseLabel = accessibilityStrings?.pause_video || "Pause video";
      const label = isPlaying ? pauseLabel : playLabel;

      this.control.dataset.isPlaying = String(isPlaying);
      this.control.setAttribute("aria-label", label);
      this.control.setAttribute("title", label);
    }
  }

  customElements.define(FEATURED_COLLECTION_VIDEO_TAG, FeaturedCollectionVideoTile);
}
